import React, { useEffect, useState, useCallback } from "react";
import { fetchAccounttypes } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Button from "../components/ui/Button";
import { DataTable2 } from "../components/ui/DataTable2";
import { Loader2 } from "lucide-react";
import FastAPIClient from "../services/apiClient";
import { API_BASE_PATH } from "../apiHelper";
import { PlusCircle, List, Save, XCircle, Info } from 'lucide-react'; // Icons for better UX
import { useNavigate } from "react-router-dom";

const client = new FastAPIClient(); // Assuming config is handled internally by client or env vars

const AccountType = () => {
    const { showToast } = useToast(); // Use our toast hook
    const navigate = useNavigate()

    const columns = [
        { key: 'label', label: 'Label' },
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Description' },
    ];

    const [tableData, setTableData] = useState([]);
    const [accountType, setAccountType] = useState({
        label: "",
        code: "",
        description: "",
        parent_id: "" // Initialize parent_id
    });
    const [user, setUser] = useState(null);
    const [parentTypes, setParentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            console.warn("User not found in local storage. Please log in.");
            showToast("Please log in to manage account types.", "info");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); // Set loading for form submission
        try {
            // Prepare payload, ensuring parent_id is null if empty string
            const payload = {
                ...accountType,
                parent_id: accountType.parent_id === "" ? null : accountType.parent_id
            };
            const response = await client.postData(`${API_BASE_PATH}/accounting/create_accounttype`, payload);
            showToast(response?.msg || "Account type created successfully!", 'success');

            const newAccountType = {
                label: response?.accounttype?.label,
                code: response?.accounttype?.code,
                description: response?.accounttype?.description,
            };
            setTableData((prevData) => [...prevData, newAccountType]);
            setAccountType({ // Reset form fields
                label: "",
                code: "",
                description: "",
                parent_id: ""
            });
            // Reload parent types as a new one might be added
            await loadAccountTypes();

        } catch (err) {
            console.error("Error creating new account type:", err);
            const errorMessage = err?.response?.data?.detail || `Error creating new account type: ${err.message || err}`;
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false); // End loading
        }
    };

    // Handle input changes for form fields
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setAccountType((prevObj) => {
            const updated = {
                ...prevObj,
                [name]: value,
            };
            if (name === 'label') {
                // Generate code from label: lowercase, trim, replace spaces with underscores, remove non-alphanumeric (except underscore), collapse multiple underscores
                updated.code = value.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "").replace(/_{2,}/g, "_");
            }
            return updated;
        });
    }, []);

    // Load account types for the table and parent type dropdown
    const loadAccountTypes = useCallback(async () => {
        setLoading(true); // Set loading for initial data fetch
        setError(null);
        try {
            const fetchedAccountTypes = await fetchAccounttypes();
            if (!Array.isArray(fetchedAccountTypes) || fetchedAccountTypes.length === 0) {
                setParentTypes([]);
                setTableData([]);
                return;
            }

            const options = fetchedAccountTypes.map((actype) => ({
                label: actype.label,
                value: actype.accounttype_id,
            }));
            setParentTypes(options);

            const tdata = fetchedAccountTypes.map((actype) => ({
                label: actype.label,
                code: actype.code,
                description: actype.description,
            }));
            setTableData(tdata);
        } catch (err) {
            console.error("Error loading account types:", err);
            const errorMessage = err.response?.data?.detail || "Failed to load account types";
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false); // End loading
        }
    }, [showToast]);

    // Effect to load account types when user is available
    useEffect(() => {
        if (user) {
            loadAccountTypes();
        }
    }, [user, loadAccountTypes]);

    // Render loading, error, or main content
    if (loading && !tableData.length) { // Show loading spinner only if no data is present yet
        return (
            <div className='flex items-center justify-center min-h-[60vh] text-center p-8 text-text-secondary'>
                <Info size={32} className="animate-bounce text-gmail-blue" />
                <p className="ml-3 text-lg">Loading account types...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gmail-red'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: {error}</p>
                <Button onClick={() => loadAccountTypes()} variant="primary">
                    Retry Loading
                </Button>
            </div>
        );
    }

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to manage account types.</p>
                {/* <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button> */}
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row flex-1 p-6 bg-gray-100 min-h-screen gap-6">
            {/* Left Pane: Create Account Type Form */}
            <div className="lg:w-1/3 flex-shrink-0 bg-white rounded-xl shadow-md p-6 h-fit sticky top-6">
                <h2 className="text-2xl font-bold text-text-primary mb-6 flex items-center gap-2">
                    <PlusCircle size={28} className="text-gmail-blue" /> Create Account Type
                </h2>
                <form onSubmit={handleSubmit}>
                    <Input
                        name="label"
                        value={accountType?.label || ""}
                        onChange={handleInputChange}
                        label="Label"
                        type="text"
                        id="label"
                        placeholder="e.g., Asset, Liability, Expense"
                        required
                    />
                    <Input
                        name="code"
                        value={accountType?.code || ""}
                        onChange={handleInputChange}
                        label="Code"
                        type="text"
                        id="code"
                        placeholder="account_type_code"
                        readOnly // Code is auto-generated
                        className="bg-gray-50 cursor-not-allowed"
                    />
                    <Select
                        name="parent_id"
                        value={accountType?.parent_id || ""}
                        onChange={handleInputChange}
                        label="Parent Account Type (optional)"
                        options={parentTypes}
                        placeholder="No parent account type"
                    />
                    <Input
                        name="description"
                        value={accountType?.description || ""}
                        onChange={handleInputChange}
                        label="Brief Description"
                        type="textarea" // Use type="textarea" for multiline input
                        id="description"
                        placeholder="Describe the purpose of this account type"
                    />
                    <div className="flex justify-end mt-6">
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex items-center gap-2 px-6 py-3 text-lg"
                            disabled={loading} // Disable button during submission
                        >
                            {loading ? (
                                <Loader2 className="animate-spin h-5 w-5" />
                            ) : (
                                <Save size={20} />
                            )}
                            {loading ? 'Saving...' : 'Save Account Type'}
                        </Button>
                    </div>
                </form>
            </div>

            {/* Right Pane: Account Types Table */}
            <div className="lg:w-2/3 flex-grow bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-text-primary mb-6 flex items-center gap-2">
                    <List size={28} className="text-gmail-green" /> Existing Account Types
                </h2>
                <DataTable2
                    columns={columns}
                    data={tableData}
                    loading={loading} // Pass loading state to DataTable
                    error={error} // Pass error state to DataTable
                    emptyMessage="No account types created yet."
                // You can add row actions here if needed, similar to AccountsTable
                // rowActions={[{ label: "Edit", key: "edit" }]}
                // onAction={(key, row) => console.log(key, row)}
                />
            </div>
        </div>
    );
};

export default AccountType;
